<template>
  <div>
    <!-- Store -->
    <store-tile
      data-cy="store-tile"
      :allow-edit-association-member-number="
        $store.getters.userCanSetStoreAssociationMemberNumber
      "
      :expanded="site.expanded"
      :num-radiodecoders="site.num_radiodecoders_including_slaves"
      :num-touchdecoders="site.num_touchdecoders"
      :num-tvdecoders="site.num_tvdecoders_including_slaves"
      :offline-decoders="offlineDecoders"
      :show-divider="showDivider"
      :show-new-badge="storeIsNew || (atLeastOneDecoderIsNew && !site.expanded)"
      :show-offline-marker="atLeastOneDecoderOffline"
      :store="site.store"
      :title="$t('expandCollapseAll')"
      @click.exact="$emit('toggle-expand')"
      @click.shift.exact="$emit('toggle-expand-all')"
    />
    <!-- Decoders -->
    <transition
      name="list-transition"
      mode="out-in"
    >
      <v-list
        v-if="site.expanded"
        class="list inset-left"
        dense
        subheader
      >
        <div
          v-for="(decoder, index) in sortedDecoders"
          :key="decoder.id"
        >
          <v-list-tile
            avatar
            data-cy="decoder-tile"
            class="py-1"
            @click="goToDecoder(decoder)"
          >
            <!-- Decoder Icon -->
            <v-list-tile-avatar>
              <v-icon
                v-if="!decoder.is_online"
                class="error--text offline-decoder"
                :title="$t('decoderOffline')"
              >
                error
              </v-icon>
              <v-icon v-if="decoder.ctype === 'radio'">
                radio
              </v-icon>
              <icon-tv-decoder
                v-if="decoder.ctype === 'tv'"
                :tvdecoder="decoder"
              />
              <v-icon v-if="decoder.ctype === 'touch'">
                touch_app
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <!-- Description, which includes any slave player descriptions -->
              <v-list-tile-title class="pseudo-anchor--inverted">
                <decoder-description :decoder="decoder" />
              </v-list-tile-title>
              <!-- Tags -->
              <v-list-tile-sub-title
                v-if="
                  decoder.tags &&
                    decoder.tags.length > 0 &&
                    $userprofile &&
                    $userprofile.can_use_tagging
                "
              >
                <tag-list
                  :tags="decoder.tags"
                  collapsible
                />
              </v-list-tile-sub-title>
              <!-- Number of Entries (or for touch: the webpage URL) -->
              <v-list-tile-sub-title class="decoder-subtitle">
                <div
                  v-if="
                    decoder.ctype === 'radio' ||
                      (decoder.ctype === 'tv' && !decoder.is_menu_player) ||
                      (decoder.is_menu_player && decoder.entries > 0)
                  "
                  :class="{
                    'error--text': !decoder.entries && !decoder.is_menu_player,
                    'text--lighten-1': !decoder.entries,
                  }"
                >
                  {{ decoder.entries }}
                  <span v-if="decoder.ctype === 'tv'">{{
                    decoder.entries === 1 ? $t('clip') : $t('clips')
                  }}</span>
                  <span v-if="decoder.ctype === 'radio'">{{
                    decoder.entries === 1 ? $t('spot') : $t('spots')
                  }}</span>
                </div>
                <div v-else-if="decoder.ctype === 'touch'">
                  {{ decoder.webpage }}
                </div>
              </v-list-tile-sub-title>
              <v-list-tile-sub-title
                v-if="
                  decoder.ctype === 'tv' &&
                    !decoder.entries &&
                    !decoder.read_only &&
                    !decoder.is_menu_player
                "
              >
                <a
                  href="javascript:void(0)"
                  @click.stop="goToAddSpotToDecoder(decoder.ctype, decoder.id)"
                >
                  {{
                    $vuetify.breakpoint.xsOnly
                      ? $t('tvPlayerAddContentNow')
                      : $t('noDecoderEntries') +
                        ' - ' +
                        $t('activateContentNow')
                  }}
                </a>
              </v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-avatar
              v-if="decoder.read_only"
              :class="{'minimal-list-avatar': $vuetify.breakpoint.width < 400}"
            >
              <v-icon
                class="warning--text clickable"
                @click.stop="alertReadOnly()"
              >
                lock
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-avatar
              :class="{'minimal-list-avatar': $vuetify.breakpoint.width < 400}"
            >
              <v-icon class="grey--text">
                chevron_right
              </v-icon>
            </v-list-tile-avatar>
            <img
              v-if="decoder.is_new"
              :src="$store.state.common.newBadgeURL"
              class="newbadge"
            >
          </v-list-tile>
          <v-divider v-if="index !== sortedDecoders.length - 1" />
        </div>
        <!-- Show a note if there are no decoders -->
        <template v-if="sortedDecoders.length === 0">
          <v-list-tile>
            <v-list-tile-sub-title>
              {{ $t('noDecoders') }}
            </v-list-tile-sub-title>
          </v-list-tile>
        </template>
      </v-list>
    </transition>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';
import {alertReadOnly} from '@/mixins/commonEntry.js';

import DecoderDescription from '@/components/DecoderDescription';
import IconTvDecoder from '@/components/IconTvDecoder';
import StoreTile from '@/components/StoreTile';
import TagList from '@/components/TagList';

export default {
  components: {
    DecoderDescription,
    IconTvDecoder,
    StoreTile,
    TagList,
  },
  props: {
    site: {
      type: Object,
      required: true,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    sortedDecoders() {
      const decoders = []
        .concat(this.site.radiodecoders)
        .concat(this.site.tvdecoders)
        .concat(this.site.touchdecoders);
      return orderBy(decoders, 'description');
    },
    storeIsNew() {
      return this.site.store.is_new;
    },
    atLeastOneDecoderIsNew() {
      return this.sortedDecoders.filter((decoder) => decoder.is_new).length > 0;
    },
    offlineDecoders() {
      if (!this.site || !this.site.status_report) {
        return [];
      }
      return this.site.status_report.offline_decoders;
    },
    atLeastOneDecoderOffline() {
      return this.offlineDecoders.length > 0;
    },
  },
  methods: {
    goToDecoder(decoder) {
      this.$router.push({
        name: 'decoder',
        params: {
          decoderId: decoder.id,
          ctype: decoder.ctype,
        },
      });
    },
    goToAddSpotToDecoder(ctype, decoderId) {
      let path = '/player/' + ctype + '/' + decoderId + '/add';
      this.$router.push(path);
    },
    alertReadOnly: alertReadOnly,
  },
};
</script>

<style scoped>
.list {
  transform-origin: top;
}

.list-transition-enter-active,
.list-transition-leave-active {
  transition: all 0.2s ease-in-out;
}
.list-transition-enter,
.list-transition-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.offline-decoder {
  position: absolute;
  left: -1.5em;
  font-size: 1.25em !important;
}

.decoder-subtitle {
  display: flex;
  justify-content: space-between;
}

.minimal-list-avatar {
  min-width: auto !important;
  width: auto !important;
  min-height: auto !important;
  height: auto !important;
}
.minimal-list-avatar > i {
  min-width: auto !important;
  width: auto !important;
  min-height: auto !important;
  height: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Site.vue